<template>
  <div class="geo-adjust-container" :style="{height: containerHeight + 'px'}">
    <!-- 左侧国家/语言管理 -->
    <div class="left-panel">
      <country-lang-manager
          ref="countryLangManager"
          @selection-change="handleSelectionChange"
          @sync-languages="handleSyncLanguages"/>
    </div>

    <!-- 右侧地区管理 -->
    <div class="right-panel">
      <ankh-list :external-height="48">
        <template #btn-group>
          <el-button @click="handleSaveSubmit">{{ $t('保存更改') }}</el-button>
          <el-button @click.native="$refs.treeTable.handleChangeExpandState()">
            {{ expandState ? $t('收起') : $t('展开') }}
          </el-button>
          <el-button @click.native="handleBatchDelete">{{ $t('批量删除') }}</el-button>
        </template>

        <template #search-form>
          <el-form-item :label="$t('名称')">
            <el-input :placeholder="$t('名称')" v-model="searchForm.name" clearable/>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="handleSearch()">{{ $t('搜索') }}</el-button>
            <el-button @click="resetEmpty">{{ $t('重置') }}</el-button>
          </el-form-item>
        </template>

        <template #table="{height}">
          <tree-table :column-list="columnList"
                      :data.sync="tableData"
                      :expand.sync="expandState"
                      :height="height - 18" ref="treeTable"
                      :row-template="rowTemplate">

            <template #names="{row}">
              <div style="display: flex; gap: 6px">
                <MultiLangInput v-model="row.names" :default-language="lang" :show-status="false" :language-list="langs"
                                :show-select="false"/>
                <el-link type="primary" :underline="false" @click="$refs.treeTable.handleAddRow(row)">{{ $t('新增子级') }}</el-link>
              </div>
            </template>

            <template #refGeo="{row}">
              <div>
                <el-select v-model="row.refGeo" :placeholder="$t('请选择关联区域')" clearable v-if="!row.sub || !row.sub.length">
                  <el-option v-for="item in refGeoOptions" :key="item.code" :label="item.name"
                             :value="item.code"></el-option>
                </el-select>
              </div>
            </template>

            <template #operate="{row}">
              <div class="fb-link-flex">
                <el-link type="danger" :underline="false" @click="handleDelete(row)">{{ $t('删除') }}</el-link>
              </div>
            </template>
          </tree-table>
        </template>
      </ankh-list>
    </div>
  </div>
</template>

<script>
import {request} from "@/config/request";
import {handleDeleteTip, handleDeleteTreeNode} from "@/utils/levelUtils";
import AnkhList from "@/components/list/AnkhList.vue";
import TreeTable from "@/components/tree-table/TreeTable.vue";
import CountryLangManager from "@/components/geo-adjust/CountryLangManager.vue";
import {resetEmpty} from "@/utils/common";
import MultiLangInput from "@/components/MultiLangInput.vue";

export default {
  name: "GeoAdjustList",
  components: {MultiLangInput, TreeTable, AnkhList, CountryLangManager},
  data() {
    return {
      searchForm: {},
      tableData: [],
      selection: [],
      containerHeight: 0,
      currentParentCode: null, // 当前选中的父级code

      expandState: true,
      listRef: null,

      // 关联区域选项数据（模拟数据）
      refGeoOptions: [],

      columnList: [
        {label: this.$t('名称'), prop: "names", fixed: 'left'},
        {label: this.$t('关联区域'), prop: "refGeo"},
        {label: this.$t('创建时间'), prop: "createTimeStr"},
        {label: this.$t('操作'), prop: "operate"},
      ],

      // 新增row 需要的字段数据
      rowTemplate: {
        names: {},
        refGeo: "",
        createTimeStr: this.$moment().format("YYYY-MM-DD HH:mm:ss"),
        expand: "yes",
      },

      lang: ""
    }
  },
  methods: {
    resetEmpty,

    // 加载区域数据
    async loadGeoList() {
      try {
        const res = await request(this.$api.geo.list, {onPage: false});
        this.refGeoOptions = res.geos;
      } catch (err) {
        console.error(err);
      }
    },

    // 处理国家/语言选择变化
    async handleSelectionChange(selection) {
      console.log(this.$t('当前选中的国家/语言:'), selection)

      // 检查是否是1级切换（国家切换）
      const isCountryChange = this.isCountryChange(selection)

      // 只有1级切换时才检查未保存的更改
      if (isCountryChange && this.hasUnsavedChanges()) {
        try {
          const shouldSave = await this.showSaveConfirmDialog()
          if (shouldSave === 'save') {
            // 用户选择保存，先保存当前更改
            await this.handleSaveSubmit()
          } else if (shouldSave === 'cancel') {
            // 用户取消，不切换
            return
          }
          // shouldSave === 'dont-save' 时继续切换
        } catch (error) {
          // 用户取消或保存失败，不切换语言
          return
        }
      }

      // selection 包含: { code, language, name }
      // 保存当前选中的父级code
      this.currentParentCode = selection ? selection.code : null

      if (isCountryChange) {
        // 1级切换：请求 region/tree 接口
        if (selection && selection.code) {
          await this.loadRegionTreeData(selection.code)
        }
      } else {
        // 2级切换：只更新语言，不请求接口
        if (selection) {
          this.lang = selection.language;
        }
      }
    },

    // 加载区域树数据
    async loadRegionTreeData(parentCode) {
      try {
        const params = {
          parentCode: parentCode,
          ...this.searchForm,
        }
        const res = await request(this.$api.region.tree, params)
        const treeData = res.tree || []

        // 转换数据格式以适配表格显示
        this.tableData = this.transformRegionTreeData(treeData)
      } catch (error) {
        console.error(this.$t('获取区域树数据失败:'), error)
        this.$message.error(this.$t('获取区域数据失败'))
        this.tableData = []
      }
    },

    // 转换区域树数据格式
    transformRegionTreeData(treeData) {
      return treeData.map((item) => {
        return {
          code: item.code,
          names: item.names || {},
          refGeo: item.refGeo,
          createTimeStr: this.$moment().format('YYYY-MM-DD HH:mm:ss'), // 如果没有创建时间字段，使用当前时间
          expand: 'yes',
          isNewAdd: false,
          sub: item.children ? this.transformRegionTreeData(item.children) : []
        }
      })
    },


    // 构建区域保存数据
    buildRegionSaveData(tableData, parentCode = null, level = 2) {
      return tableData.map((item, index) => {
        // 确定当前项的parentCode
        const currentParentCode = parentCode || this.currentParentCode

        // 处理子级数据 - 对于新增项，子级的parentCode应该为空字符串
        let children = []
        if (item.sub && item.sub.length > 0) {
          // 如果当前项是新增项，子级的parentCode应该为空字符串
          const childParentCode = item.isNewAdd ? '' : item.code
          children = this.buildRegionSaveData(item.sub, childParentCode, level + 1)
        }

        return {
          parentCode: currentParentCode,
          level: level,
          sort: index + 1,
          refGeo: item.refGeo || '',
          code: item.isNewAdd ? '' : item.code, // 新增项code为空字符串
          children: children,
          names: item.names || {}
        }
      })
    },
    // 计算容器高度
    handleResizeContainer() {
      const wrapper = document.querySelector(".tv-main-body");
      if (wrapper) {
        // 减去32px的padding和16px的额外边距
        this.containerHeight = wrapper.clientHeight - 48;
      }
    },
    // 保存
    async handleSaveSubmit() {
      try {
        // 检查是否有选中的父级
        if (!this.currentParentCode) {
          this.$message.warning(this.$t('请先选择一个国家/语言'));
          return;
        }

        // 构建保存数据
        const saveData = this.buildRegionSaveData(this.tableData);

        // 提交数据到后端
        await request(this.$api.region.save, saveData, {isJson: true});
        this.$message.success(this.$t('保存成功'));
        await this.handleSearch();
      } catch (e) {
        console.log(e);
      }
    },

    // 处理数据
    handleBuildParams(list) {
      return list.map((item, index) => {
        let sub = [];
        if (item.sub && item.sub.length) {
          sub = this.handleBuildParams(item.sub);
        }
        return {
          name: item.name,
          refGeo: item.refGeo,
          code: item.isNewAdd ? null : item.code,
          sort: index + 1,
          expand: item.expand,
          sub,
        }
      })
    },

    async handleDelete(row) {
      try {
        await handleDeleteTip();
        if (!row.isNewAdd) {
          await request(this.$api.region.delete, {code: row.code});
        }
        this.tableData = handleDeleteTreeNode([row.code], this.tableData);
        this.$message.success(this.$t('操作成功'));
      } catch (e) {
        console.error(e);
      }
    },

    async handleBatchDelete() {
      const selected = this.$refs.treeTable.handleGetSelected();
      if (!selected.length) {
        this.$message({
          type: 'warning',
          message: this.$t('未选择记录')
        });
        return;
      }
      try {
        await handleDeleteTip();
        let codes = selected.filter(f => !f.isNewAdd).map(item => item.code);
        if (codes.length) {
          await request(
              this.$api.region.batchDelete,
              codes,
              {isJson: true},
          );
        }
        const delCodes = selected.map(item => item.code);
        this.$message.success(this.$t('操作成功'));
        this.tableData = handleDeleteTreeNode(delCodes, this.tableData);
      } catch (e) {
        console.error(e);
      }
    },

    async handleSearch() {
      try {
        // 如果有选中的国家，则加载对应的区域数据
        const currentSelection = this.$refs.countryLangManager?.getCurrentSelection()
        if (currentSelection && currentSelection.code) {
          await this.loadRegionTreeData(currentSelection.code)
        } else {
          // 如果没有选中任何国家，清空表格数据
          this.tableData = []
        }
      } catch (e) {
        console.error(e);
      }
    },

    // 检查是否有未保存的更改
    hasUnsavedChanges() {
      // 检查表格数据是否有新增或修改的内容
      return this.checkTableDataForChanges(this.tableData)
    },

    // 递归检查表格数据是否有更改
    checkTableDataForChanges(data) {
      for (const item of data) {
        // 检查是否有新增项
        if (item.isNewAdd) {
          return true
        }
        // 检查多语言名称是否有修改
        if (item.names && Object.values(item.names).some(name => name && name.trim() !== '')) {
          return true
        }
        // 递归检查子项
        if (item.sub && item.sub.length > 0) {
          if (this.checkTableDataForChanges(item.sub)) {
            return true
          }
        }
      }
      return false
    },

    // 显示保存确认对话框
    showSaveConfirmDialog() {
      return new Promise((resolve, reject) => {
        this.$confirm(
            this.$t('检测到有未保存的更改，是否保存当前内容？'),
            this.$t('提示'),
            {
              confirmButtonText: this.$t('保存'),
              cancelButtonText: this.$t('不保存'),
              type: 'warning',
              distinguishCancelAndClose: true
            }
        ).then(() => {
          // 用户选择保存
          resolve('save')
        }).catch(action => {
          if (action === 'cancel') {
            // 用户选择不保存
            resolve('dont-save')
          } else {
            // 用户关闭对话框
            reject(new Error(this.$t('用户取消操作')))
          }
        })
      })
    },


    // 判断是否是1级切换（国家切换）
    isCountryChange(selection) {
      if (!selection) return false
      // 如果当前没有选中的国家，或者选中的国家code不同，则是1级切换
      return !this.currentParentCode || this.currentParentCode !== selection.code
    },

    // 处理语言同步事件（来自CountryLangManager组件）
    async handleSyncLanguages() {
      try {
        // 检查是否有选中的国家
        if (!this.currentParentCode) {
          this.$message.warning(this.$t('请先选择一个国家/语言'));
          return;
        }

        // 检查是否有数据需要同步
        if (!this.tableData || this.tableData.length === 0) {
          this.$message.warning(this.$t('没有数据需要同步'));
          return;
        }

        // 执行同步
        this.syncLanguagesData(this.tableData);
        this.$message.success(this.$t('同步完成'));
      } catch (error) {
        console.error('同步语言失败:', error);
        this.$message.error(this.$t('同步失败'));
      }
    },

    // 递归同步语言数据
    syncLanguagesData(data) {
      data.forEach(item => {
        if (item.names && typeof item.names === 'object') {
          // 获取源语言的内容（优先使用zh-Hans，如果没有则使用第一个有内容的语言）
          let sourceContent = item.names['zh-Hans'];
          let sourceLangCode = 'zh-Hans';
          
          // 如果zh-Hans没有内容，则使用第一个有内容的语言作为源
          if (!sourceContent || sourceContent.trim() === '') {
            for (const lang of this.langs) {
              const content = item.names[lang.code];
              if (content && content.trim() !== '') {
                sourceContent = content;
                sourceLangCode = lang.code;
                break;
              }
            }
          }
          
          if (sourceContent && sourceContent.trim() !== '') {
            // 遍历所有语言，只同步空内容
            this.langs.forEach(lang => {
              const langCode = lang.code;
              // 跳过源语言本身，只同步其他语言
              if (langCode !== sourceLangCode) {
                // 只有当目标语言内容为空时才同步
                if (!item.names[langCode] || item.names[langCode].trim() === '') {
                  item.names[langCode] = sourceContent;
                }
              }
            });
          }
        }
        
        // 递归处理子级数据
        if (item.sub && item.sub.length > 0) {
          this.syncLanguagesData(item.sub);
        }
      });
    },

  },
  mounted() {
    this.handleResizeContainer();
    this.handleSearch();
    this.loadGeoList();
    window.addEventListener("resize", this.handleResizeContainer);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResizeContainer);
  },
  computed: {
    langs() {
      const list = this.$store.getters.getDictByFlag('LANGUAGE-VERSIONFRONT');
      return list.map(item => {
        return {
          code: item.dictValue,
          name: item.name,
        }
      })
    }
  }
}
</script>

<style scoped>
.geo-adjust-container {
  display: flex;
  background: #f5f5f5;
  padding: 16px;
  gap: 16px;
  overflow: hidden;
}

.left-panel {
  width: 320px;
  min-width: 320px;
  height: 100%;
  overflow: hidden;
}

.right-panel {
  flex: 1;
  height: 100%;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

::v-deep .cell {
  display: flex;
  align-items: center;
}

/* 局部样式：给右侧按钮区域添加padding，不影响全局AnkhList组件 */
.right-panel ::v-deep .btn-group {
  padding: 20px 20px 0 20px;
}

/* 响应式布局 */
@media (max-width: 1200px) {
  .left-panel {
    width: 280px;
    min-width: 280px;
  }
}

@media (max-width: 768px) {
  .geo-adjust-container {
    flex-direction: column;
    height: auto;
  }

  .left-panel {
    width: 100%;
    min-width: auto;
    height: 300px;
  }

  .right-panel {
    height: auto;
  }
}
</style>
